import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Landmark,
  IndianRupee,
  ArrowUpRight,
  Calculator,
  ArrowLeft,
} from "lucide-react";
import { Button } from "@/common/components/ui/button";
import { Card, CardContent } from "@/common/components/ui/card";
import { CompactCalculatorRow } from "../CompactCalculatorRow";
import { calculateLumpsum, formatCurrency } from "../../utils/calculations";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import ProductLayout from "@/common/components/ProductLayout";

export const LumpsumCalculator = () => {
  const navigate = useNavigate();
  const [lsAmount, setLsAmount] = useState(100000);
  const [lsRate, setLsRate] = useState(12);
  const [lsYears, setLsYears] = useState(5);

  const lsResult = useMemo(
    () => calculateLumpsum(lsAmount, lsRate, lsYears),
    [lsAmount, lsRate, lsYears]
  );

  const lsPieData = [
    { name: "Principal", value: lsAmount, color: "#14b8a6" },
    { name: "Returns", value: lsResult.returns, color: "#8b5cf6" },
  ];

  return (
    <ProductLayout>
      <div className="min-h-screen bg-background py-8">
        <div className="container mx-auto px-4 md:px-6">
          <div className="mb-8">
            <Button
              variant="ghost"
              onClick={() => navigate("/calculators")}
              className="mb-4 pl-0 hover:bg-transparent hover:text-primary"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Calculators
            </Button>
            <div className="flex items-center gap-4">
              <div className="rounded-lg bg-primary/10 p-3 text-primary">
                <Calculator className="h-8 w-8" />
              </div>
              <h1 className="text-3xl font-bold text-secondary">
                Lumpsum Calculator
              </h1>
            </div>
          </div>

          <div className="mx-auto max-w-7xl">
            <div className="grid gap-8 lg:grid-cols-2">
              <Card className="border-border bg-card">
                <CardContent className="space-y-6 p-6 md:p-8">
                  <div className="mb-8 flex items-center justify-between">
                    <div className="flex rounded-2xl bg-muted/50 p-1">
                      <button
                        onClick={() => navigate("/calculators/sip")}
                        className="rounded-xl px-6 py-2 text-sm font-black text-slate-500 transition-all hover:text-slate-700"
                      >
                        SIP
                      </button>
                      <button
                        onClick={() => navigate("/calculators/lumpsum")}
                        className="rounded-xl bg-white px-6 py-2 text-sm font-black text-primary shadow-sm transition-all"
                      >
                        Lumpsum
                      </button>
                    </div>
                    <div className="rounded-xl bg-purple-100 p-3">
                      <Landmark className="h-6 w-6 text-purple-600" />
                    </div>
                  </div>

                  <div className="space-y-6">
                    <CompactCalculatorRow
                      label="Total investment"
                      value={lsAmount}
                      onChange={setLsAmount}
                      min={500}
                      max={10000000}
                      step={500}
                      prefix="₹"
                      limitLabels={["₹500", "₹1 Cr"]}
                      colorClass="text-primary"
                    />

                    <CompactCalculatorRow
                      label="Expected return rate (p.a)"
                      value={lsRate}
                      onChange={setLsRate}
                      min={1}
                      max={40}
                      step={0.1}
                      suffix="%"
                      colorClass="text-green-600"
                    />

                    <CompactCalculatorRow
                      label="Time period"
                      value={lsYears}
                      onChange={setLsYears}
                      min={1}
                      max={40}
                      step={1}
                      suffix="Yrs"
                      limitLabels={["1 year", "40 years"]}
                      colorClass="text-purple-600"
                    />
                  </div>
                </CardContent>
              </Card>

              <div className="space-y-6">
                <Card className="border-border bg-muted/20">
                  <CardContent className="p-6 md:p-8">
                    <h3 className="mb-6 flex items-center gap-2 text-lg font-bold text-secondary">
                      <IndianRupee className="h-5 w-5 text-primary" />
                      Investment Summary
                    </h3>

                    <div className="mb-6 grid grid-cols-2 gap-4">
                      <div className="h-[180px]">
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              data={lsPieData}
                              cx="50%"
                              cy="50%"
                              innerRadius={45}
                              outerRadius={70}
                              paddingAngle={3}
                              dataKey="value"
                            >
                              {lsPieData.map(entry => (
                                <Cell key={entry.name} fill={entry.color} />
                              ))}
                            </Pie>
                            <Tooltip
                              formatter={(value: number | undefined) =>
                                value !== undefined ? formatCurrency(value) : ""
                              }
                            />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                      <div className="flex flex-col justify-center space-y-3">
                        {lsPieData.map(item => (
                          <div
                            key={item.name}
                            className="flex items-center gap-2"
                          >
                            <div
                              className="h-3 w-3 rounded-full"
                              style={{ backgroundColor: item.color }}
                            />
                            <span className="text-sm text-muted-foreground">
                              {item.name}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="space-y-3">
                      <div className="flex items-center justify-between rounded-xl bg-white p-4 shadow-sm">
                        <span className="text-muted-foreground">
                          Principal Amount
                        </span>
                        <span className="text-xl font-bold text-secondary">
                          {formatCurrency(lsAmount)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between rounded-xl bg-white p-4 shadow-sm">
                        <span className="flex items-center gap-2 text-muted-foreground">
                          <ArrowUpRight className="h-4 w-4 text-green-500" />
                          Estimated Returns
                        </span>
                        <span className="text-xl font-bold text-green-600">
                          {formatCurrency(lsResult.returns)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between rounded-xl bg-gradient-to-r from-purple-500 to-indigo-600 p-5 text-white shadow-lg">
                        <span className="font-semibold">Total Value</span>
                        <span className="text-2xl font-bold">
                          {formatCurrency(lsResult.futureValue)}
                        </span>
                      </div>
                      <button className="mt-6 w-full rounded-xl bg-[#00d09c] py-4 font-black text-white shadow-xl shadow-[#00d09c]/20 transition-all hover:bg-[#00b88a] active:scale-[0.98]">
                        INVEST NOW
                      </button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </div>
    </ProductLayout>
  );
};
